import { Button, Select, createListCollection } from "@chakra-ui/react"
import { useTranslate } from "@pathwright/ux/src/components/lng/useTranslate"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import "highlight.js/styles/github.css"
import { useMemo } from "react"
import { z } from "zod"
import { ResponseOrderBy } from "@pathwright/graphql-client/src/generated"
import { useDiscussionContext } from "../DiscussionProvider"

type ResponseOrderOption = {
  label: string
  value: OrderResponsesBy
}

export const orderResponsesBySchema = z.enum([
  ResponseOrderBy.LikesDesc,
  ResponseOrderBy.ActivityDesc,
  ResponseOrderBy.CreationDesc,
  ResponseOrderBy.Creation
])

export type OrderResponsesBy = z.infer<typeof orderResponsesBySchema>

const DiscussionResponsesOrderBySelect = () => {
  const { t } = useTranslate()

  const orderResponsesBy = useDiscussionContext((c) => c.orderResponsesBy)
  const setOrderResponsesBy = useDiscussionContext((c) => c.setOrderResponsesBy)

  const responseOrderOptions = useMemo(
    () =>
      createListCollection<ResponseOrderOption>({
        items: [
          {
            label: t("discussion.filters.best"),
            value: ResponseOrderBy.LikesDesc
          },
          {
            label: t("discussion.filters.active"),
            value: ResponseOrderBy.ActivityDesc
          },
          {
            label: t("discussion.filters.recent"),
            value: ResponseOrderBy.CreationDesc
          },
          {
            label: t("discussion.filters.oldest"),
            value: ResponseOrderBy.Creation
          }
        ]
      }),
    []
  )

  return (
    <Select.Root
      name="responseOrderOptions"
      positioning={{ placement: "bottom-end" }}
      collection={responseOrderOptions}
      value={[orderResponsesBy]}
      onValueChange={(e) => {
        setOrderResponsesBy(e.value[0] as ResponseOrderOption["value"])
      }}
      width="fit-content"
    >
      <Select.Control>
        <Select.Trigger asChild>
          <Button
            size="sm"
            colorPalette="gray"
            variant="subtle"
            // HACK: the Select.Trigger applies a transparent background to the button, so we need to override it.
            bg="var(--chakra-colors-color-palette-subtle)"
            border="none"
            borderRadius="full"
          >
            <Select.ValueText
              placeholder={responseOrderOptions.find(orderResponsesBy)?.label}
            />
            <Select.IndicatorGroup pos="static" p={0}>
              <Select.Indicator>
                <Pathicon icon="chevron-down" />
              </Select.Indicator>
            </Select.IndicatorGroup>
          </Button>
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content p={0}>
          {responseOrderOptions.items.map((item: ResponseOrderOption) => (
            <Select.Item key={item.value} item={item}>
              {item.label}
              <Select.ItemIndicator>
                <Pathicon icon="check" />
              </Select.ItemIndicator>
            </Select.Item>
          ))}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  )
}

export default DiscussionResponsesOrderBySelect
